import Box from '@mui/material/Box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import AlertMessages from 'common/Alert/AlertMessages';
import { useTranslation } from 'react-i18next';
import { checkPrivileges } from 'Helpers/CheckPrivileges';
import BackendPaginationTable from 'common/Table/TableWithPagination';
import { useNavigate, useParams } from 'react-router-dom';
import ComponentHeaderTwo from 'common/ComponentHeaderTwo/ComponentHeaderTwo';
import { ReactComponent as Groups  } from "assets/SideBarIcons/Groups.svg";
import { useGetServiceGroupServiceService, useAssignServiceService, useUnassignServiceService, useAssignAllServiceService, useUnassignAllServiceService } from 'subModule/src/services/servicegroup/useServiceGroup';
import ServiceGroupTabs from './ServiceGroupTabs';
import ServiceGroupServiceSearchHeader from 'components/ServiceGroup/ServiceGroupServiceSearchHeader';
import { SelectChangeEvent } from '@mui/material';
import {
    getSortParamFromColumn,
    SERVICE_SORT_FIELD_MAP,
    SortOrder,
} from 'Helpers/buildApiSortParam';
import type { BreadcrumbItem } from 'Helpers/generalBreadcrumbs';
import { pickProfileEntityTitle } from 'Helpers/pickProfileEntityTitle';
import { ServiceGroupProfileService } from 'subModule/src/core/services';

const SERVICE_GROUP_SORTABLE_COLUMNS = ['title', 'serviceCode'];

interface SearchFormData {
    [key: string]: string | any;
}
export default function ServiceGroupServiceTable() {
    const params = useParams();
    const groupId = params?.id;
    const navigate = useNavigate();
    const [groupTitle, setGroupTitle] = useState('');
    const [pageNo, setPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [loading, setLoading] = useState<boolean>(false);
    const [filter, setFilter] = useState<string[]>(['title']);
    const [search, setSearch] = useState<any>({});
    const [sort, setSort] = useState<string | undefined>();
    const [sortOrderBy, setSortOrderBy] = useState('');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const [searchFormData, setSearchFormData] = useState<SearchFormData>({ title: '', serviceCode: '' });
    const { data: tableData, isFetching, refetch } = useGetServiceGroupServiceService({ pageNumber: pageNo, size: pageSize, ...search, sort }, +(params as any).id);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    useEffect(() => {
        if (!groupId) return;
        let active = true;
        ServiceGroupProfileService.getServiceGroupById(+groupId)
            .then((res: unknown) => {
                if (!active) return;
                const name = pickProfileEntityTitle(res);
                if (name) setGroupTitle(name);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [groupId]);

    const breadcrumbItems: BreadcrumbItem[] = useMemo(
        () => [
            { labelKey: 'Items' },
            { labelKey: 'servicesGroup', href: '/services-group' },
            {
                labelKey: 'title',
                ...(groupTitle ? { label: groupTitle } : {}),
                href: groupId ? `/service-group/${groupId}` : undefined,
            },
            { labelKey: 'assignService' },
        ],
        [groupId, groupTitle],
    );

    let allServicesAssigned: unknown = tableData?.pages[0].data?.content.map(service => service.linked).every(linked => linked === true);
    const fetchData = useCallback(() => {
        refetch().then((res: any) => {
            if (!res?.data?.pages[0]?.success) enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
        });
    }, []);
    useEffect(() => {
        fetchData();
    }, [pageNo, pageSize, search, sort]);
    const handleChangePage = (pageNo: number) => {
        setPageNo(pageNo + 1);
    }
    const handleChangeRows = (pageSize: number) => {
        setPageSize(pageSize);
        setPageNo(1);
    }
    const clearSearch = () => {
        setFilter(['title']);
        setSearchFormData({ title: '', serviceCode: '' });
        if (Object.keys(search).length) {
            setSearch({});
        }
    }
    const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
        const { target: { value }, } = event;
        setFilter(typeof value === 'string' ? value.split(',') : value,);
        setSearchFormData(prevFormData =>
            Object.fromEntries(Object.keys(prevFormData).map(key =>
                [key, value.includes(key) ? prevFormData[key] : '']
            ))
        );
        if (!value.length && Object.keys(search).length) {
            clearSearch();
        }
    };
    const handleChangeSearch = (field: string, value: any) => {
        const val = value;
        setSearchFormData((prevData) => ({
            ...prevData,
            [field]: val,
        }));
    };
    const handleSubmitSearch = () => {
        const filteredSearch: Record<string, string> = {};
        Object.entries(searchFormData).forEach(([key, value]) => {
            if (value) {
                filteredSearch[key] = value?.trim();
            }
        });
        if (Object.keys(filteredSearch).length > 0 && Object.values(filteredSearch).some(value => value)) {
            setSearch(filteredSearch);
            setPageNo(1);
            setPageSize(pageSize);
        }
    };
    const handleSortChange = (orderBy: string, order: SortOrder) => {
        const nextSort = getSortParamFromColumn(orderBy, order, SERVICE_SORT_FIELD_MAP);
        if (!nextSort) return;
        setSortOrderBy(orderBy);
        setSortOrder(order);
        setSort(nextSort);
        setPageNo(1);
    };
    const assignUnassignSingleEntity = (isChecked: boolean, row: any) => {
        setLoading(true);
        if (isChecked) {
            onAssignService({ serviceGroupId: +(params as any).id, serviceId: row.id });
        } else {
            onUnassignService({ serviceGroupId: +(params as any).id, serviceId: row.id });
        }
    }
    const assignUnassignAllEntities = (isChecked: boolean) => {
        setLoading(true);
        if (isChecked) {
            onAssignAllServices({ serviceGroupId: +(params as any).id });
        } else {
            onUnassignAllServices({ serviceGroupId: +(params as any).id });
        }
    }
    const { onAssignService } = useAssignServiceService({
        onSuccess: (msg) => {
            const response: any = {};
            Object.keys(msg).forEach((key, index) => { response[key] = Object.values(msg)[index]; });
            if (response.success) {
                if (tableData?.pages[0].data.content.length === tableData?.pages[0].data.content.filter(service => service.linked).length) {
                    allServicesAssigned = true;
                }
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('service') + ' ' + t('assigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
            if (err instanceof Error) {
                console.error(err.stack);
            }
        }
    })
    const { onUnassignService } = useUnassignServiceService({
        onSuccess: (data) => {
            const response: any = {};
            Object.keys(data).forEach((key, index) => { response[key] = Object.values(data)[index]; });
            if (response.success) {
                if (tableData?.pages[0].data.content.length !== tableData?.pages[0].data.content.filter(service => service.linked).length) {
                    allServicesAssigned = false;
                }
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('service') + ' ' + t('unassigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            console.log("err====,  ", err);
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
        },
    });
    const { onAssignAllServices } = useAssignAllServiceService({
        onSuccess: (msg) => {
            const response: any = {};
            Object.keys(msg).forEach((key, index) => { response[key] = Object.values(msg)[index]; });
            if (response.success) {
                allServicesAssigned = true;
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('All') + ' ' + t('services') + ' ' + t('assigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
            if (err instanceof Error) {
                console.error(err.stack);
            }
        }
    })
    const { onUnassignAllServices } = useUnassignAllServiceService({
        onSuccess: (data) => {
            const response: any = {};
            Object.keys(data).forEach((key, index) => { response[key] = Object.values(data)[index]; });
            if (response.success) {
                allServicesAssigned = false;
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('All') + ' ' + t('services') + ' ' + t('unassigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            console.log("err====,  ", err);
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
        },
    });

    return (
        <Box>
            <ComponentHeaderTwo
                showBtn={true}
                showSaveBtn={false}
                onClick={() => navigate('/services-group')}
                heading={t('servicesGroup')}
                icon={<Groups height={28} width={28} />}
                btnText={"back"}
                btnType={"back"}
                showCreateBtn={false}
                breadcrumbItems={breadcrumbItems}
            />
            <Box>
                <div className='flex'>
                    {params?.id ? <ServiceGroupTabs params={params} selectedIndex={1} /> : null}
                    <BackendPaginationTable
                        columns={[
                            { id: 'title', label: 'title', numeric: false },
                            { id: 'serviceCode', label: 'serviceCode', numeric: false },
                        ]}
                        data={tableData?.pages[0]?.data?.content || []}
                        pageNo={pageNo}
                        pageSize={pageSize}
                        tableData={tableData?.pages[0]?.data}
                        showCheckbox={true}
                        showActions={true}
                        enableSorting={true}
                        serverSideSorting={true}
                        sortOrderBy={sortOrderBy}
                        sortOrder={sortOrder}
                        onSortChange={handleSortChange}
                        sortableColumnIds={SERVICE_GROUP_SORTABLE_COLUMNS}
                        showHeader={true}
                        showSearch={true}
                        showFilter={true}
                        showCreateButton={false}
                        actions={[]}
                        handleChangePage={handleChangePage}
                        handleChangeRows={handleChangeRows}
                        assignUnassignSingleEntity={assignUnassignSingleEntity}
                        assignUnassignAllEntities={assignUnassignAllEntities}
                        allEntitiesAssigned={allServicesAssigned}
                        loadingSearch={isFetching || loading}
                        assignPrivilege={checkPrivileges('ROLE_SERVICEGROUP_SERVICE_ASSIGN')}
                        unassignPrivilege={checkPrivileges('ROLE_SERVICEGROUP_SERVICE_UNASSIGN')}
                        headerComponent={<ServiceGroupServiceSearchHeader 
                            searchFormData={searchFormData}
                            handleChangeFilter={handleChangeFilter}
                            handleChangeSearch={handleChangeSearch}
                            handleSubmitSearch={handleSubmitSearch}
                            clearSearch={clearSearch}
                            search={search}
                            {...{filter}}
                        />}
                    />
                </div>
            </Box>
        </Box>
    )
}